import { useCallback, useEffect, useMemo, useState } from 'react'

import API from '../api/baseUrl'
import { getAuthToken } from '../utils/auth-token'

interface FetchedDataType {
  data: unknown
  error: boolean
  isLoading: boolean
}

const useFetch = (url: string, full?: boolean) => {
  const [fetchedData, setFetchedData] = useState<FetchedDataType>({
    // Set initial fetch state to an empty object
    data: [],
    error: false,
    isLoading: true,
  })
  const controller = useMemo(() => new AbortController(), [])

  const fetchData = useCallback(async () => {
    // Fetch data from API
    try {
      const accessToken = await getAuthToken() // Get Firebase auth token
      const options = {
        headers: {
          ...(import.meta.env.MODE && { env: import.meta.env.MODE }),
          Authorization: accessToken as unknown as string,
        },
      }
      const response = await API.get(url, options) // Get data from API
      const data = await response.data // Get data from response
      setFetchedData({
        // Set fetched data to state
        ...(full ? { data } : { data: data.items ? data.items : data }),
        error: false,
        isLoading: false,
      })
    } catch (error) {
      console.log('error ocurred', error)
      setFetchedData({
        // Set fetch state to error
        data: [],
        error: true,
        isLoading: false,
      })
    }
  }, [full, url])

  const refetch = () => {
    // Create a new refetch function
    setFetchedData({
      // Set fetched data to initial state
      data: [],
      error: false,
      isLoading: true,
    })
    fetchData() // Call fetchData function
  }

  useEffect(() => {
    // Fetch data on mount
    fetchData()
    return () => controller.abort() // Abort fetch on unmount
  }, [fetchData, controller])

  const { data, error, isLoading } = fetchedData // Get data from state
  return { data, error, isLoading, refetch } // Return data, state and refetch function
}

export default useFetch
